import type { AdminRole } from '@diora/contracts';
import clsx from 'clsx';
import {
  Building2,
  ClipboardList,
  FileText,
  Globe,
  Image,
  LayoutDashboard,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  QrCode,
  Settings,
  Sun,
  Tag,
  UtensilsCrossed,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';
import { useTheme } from '@/providers/theme';
import { ErrorBoundary } from './error-boundary';
import { Button } from './ui';

interface NavEntry {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles?: AdminRole[];
}

interface NavGroup {
  label: string;
  entries: NavEntry[];
}

function navigation(t: ReturnType<typeof useTranslation>['t']): NavGroup[] {
  return [
    {
      label: t('nav.overview'),
      entries: [{ to: '/', label: t('nav.dashboard'), icon: LayoutDashboard }],
    },
    {
      label: t('nav.menuGroup'),
      entries: [
        { to: '/menu/categories', label: t('nav.categories'), icon: UtensilsCrossed },
        { to: '/menu/items', label: t('nav.items'), icon: ClipboardList },
        { to: '/menu/addons', label: t('nav.addons'), icon: Tag },
        { to: '/offers', label: t('nav.offers'), icon: Tag },
      ],
    },
    {
      label: t('nav.placeGroup'),
      entries: [{ to: '/branches', label: t('nav.branches'), icon: Building2 }],
    },
    {
      label: t('nav.contentGroup'),
      entries: [
        { to: '/content/home', label: t('nav.home'), icon: FileText },
        { to: '/content/about', label: t('nav.about'), icon: FileText },
        { to: '/content/faqs', label: t('nav.faqs'), icon: FileText },
        { to: '/media', label: t('nav.media'), icon: Image },
      ],
    },
    {
      label: t('nav.systemGroup'),
      entries: [
        { to: '/settings', label: t('nav.settings'), icon: Settings },
        { to: '/users', label: t('nav.users'), icon: Users, roles: ['OWNER'] },
        { to: '/qr', label: t('nav.qr'), icon: QrCode },
        { to: '/audit', label: t('nav.audit'), icon: ClipboardList, roles: ['OWNER'] },
      ],
    },
  ];
}

export function AppShell() {
  const { t } = useTranslation();
  const { user, signOut, can } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // A route change closes the mobile drawer; leaving it open over the new page
  // is the classic way a phone user loses their place.
  useEffect(() => setNavOpen(false), [location.pathname]);

  /*
   * Escape closes the drawer.
   *
   * It did not, and the drawer covers half the screen: without this the only
   * ways out were to tab through fourteen links or to find the toggle again.
   * Focus is already on the toggle — the button precedes the panel in the
   * document, so opening it leaves focus somewhere sensible and Tab walks
   * forward into the links — which is why this needs no focus trap and no
   * focus restoration, unlike the website's header where the panel comes first.
   */
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const groups = navigation(t).map((group) => ({
    ...group,
    entries: group.entries.filter((entry) => !entry.roles || can(...entry.roles)),
  }));

  return (
    <div className="dio-shell">
      <a href="#main" className="skip-link dio-btn dio-btn--primary dio-btn--sm">
        {t('app.skipToContent')}
      </a>

      <TopBar onToggleNav={() => setNavOpen((open) => !open)} navOpen={navOpen} />

      <div className="dio-shell__body">
        {/* Mouse-only: Escape closes the drawer and the toggle is a real button,
            so this needs no name of its own — and giving it one would put a
            second "close menu" in the tab order next to the first. */}
        {navOpen && (
          <button
            type="button"
            className="dio-shell__scrim"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setNavOpen(false)}
          />
        )}

        <nav
          className="dio-sidebar"
          // Whether the drawer is open is markup; whether it is a drawer at all
          // is the stylesheet's call. A responsive utility here would have to
          // agree with the brand layer's breakpoint forever.
          data-open={navOpen || undefined}
          aria-label={t('app.mainNav')}
        >
          {/* No spacing utility on the group wrapper: `.dio-nav-group` owns the
              gap above a group. Two owners of one gap is how it reached ~48px —
              a `mb-5` here, 16px of padding there, and the UA's own paragraph
              margin on the label underneath both. */}
          {groups.map((group) => (
            <div key={group.label}>
              <p className="dio-nav-group">{group.label}</p>
              {group.entries.map((entry) => (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.to === '/'}
                  className={({ isActive }) =>
                    clsx('dio-nav-item', isActive && 'dio-nav-item--active')
                  }
                >
                  <entry.icon className="dio-icon" aria-hidden="true" />
                  <span>{entry.label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          <div className="dio-sidebar__footer">
            <p className="dio-meta">{t('app.signedInAs')}</p>
            <p className="font-medium">{user?.displayName}</p>
            <p className="dio-meta">{user && t(`users.${user.role}`)}</p>
            <Button variant="ghost" size="sm" onClick={() => void signOut()}>
              <LogOut className="dio-icon dio-icon--directional" aria-hidden="true" />
              {t('app.signOut')}
            </Button>
          </div>
        </nav>

        {/* The content pane is the scroll container, not the document, which is
            what lets the sidebar and the page header stay put. */}
        <main id="main" tabIndex={-1} className="dio-shell__content">
          <div className="dio-page">
            {/* Keyed on the path so leaving a broken screen clears the error —
                otherwise the boundary stays tripped and every later page shows
                the crash from the one before it. */}
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}

function TopBar({ onToggleNav, navOpen }: { onToggleNav: () => void; navOpen: boolean }) {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  const { choice, setChoice } = useTheme();

  return (
    <header className="dio-header sticky top-0 z-sticky">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className="dio-shell__nav-toggle"
          onClick={onToggleNav}
          aria-expanded={navOpen}
          aria-label={navOpen ? t('app.closeMenu') : t('app.openMenu')}
        >
          {navOpen ? <X className="dio-icon" aria-hidden="true" /> : <MenuIcon className="dio-icon" aria-hidden="true" />}
        </Button>

        {/* The wordmark is artwork, never a translated string. */}
        <span className="dio-header__brand" translate="no">
          {t('app.name')}
        </span>
        <span className="dio-meta hidden sm:inline">{t('app.dashboard')}</span>
      </div>

      <div className="flex items-center gap-2">
        <LanguageToggle locale={locale} onChange={setLocale} />
        <ThemeToggle choice={choice} onChange={setChoice} />
      </div>
    </header>
  );
}

function LanguageToggle({
  locale,
  onChange,
}: {
  locale: 'ar' | 'en';
  onChange: (next: 'ar' | 'en') => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="dio-segmented" role="group" aria-label={t('locale.label')}>
      {(['ar', 'en'] as const).map((option) => (
        <button
          key={option}
          type="button"
          className={clsx('dio-segmented__btn', locale === option && 'dio-tab--active')}
          aria-pressed={locale === option}
          onClick={() => onChange(option)}
          // Each label is set in its own language, so "English" never renders
          // in an Arabic face and vice versa.
          lang={option}
        >
          {t(`locale.${option}`)}
        </button>
      ))}
    </div>
  );
}

function ThemeToggle({
  choice,
  onChange,
}: {
  choice: 'system' | 'light' | 'dark';
  onChange: (next: 'system' | 'light' | 'dark') => void;
}) {
  const { t } = useTranslation();
  const options = [
    { value: 'light', icon: Sun, label: t('theme.light') },
    { value: 'dark', icon: Moon, label: t('theme.dark') },
    { value: 'system', icon: Monitor, label: t('theme.system') },
  ] as const;

  return (
    <div className="dio-segmented" role="group" aria-label={t('theme.label')}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={clsx('dio-segmented__btn', choice === option.value && 'dio-tab--active')}
          aria-pressed={choice === option.value}
          onClick={() => onChange(option.value)}
          title={option.label}
        >
          <option.icon className="dio-icon" aria-hidden="true" />
          <span className="sr-only">{option.label}</span>
        </button>
      ))}
    </div>
  );
}

/** A page heading with optional actions, used by every screen. */
export function PageHeader({
  title,
  description,
  actions,
  width,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * `narrow` caps this page's content at a readable measure.
   *
   * The screens that are forms want it; the screens that are lists, grids and
   * tables want the whole window. The page says which, because a list of form
   * routes kept in the shell is wrong the first time someone adds one — the
   * brand layer reads this attribute off the header and caps its siblings.
   */
  width?: 'narrow';
}) {
  return (
    <div className="dio-page__header" data-width={width}>
      <div>
        <h1 className="dio-page__title">{title}</h1>
        {description && <p className="dio-hint mt-1">{description}</p>}
      </div>
      {actions && <div className="dio-page__actions">{actions}</div>}
    </div>
  );
}

/** `lucide-react` icons carry no accessible name; every use passes aria-hidden. */
export const Icons = { Globe };
